import ApiServices, { InstanceKeys } from "@/api/apexchat.api.services";
import { Import } from "@/api/endpoints";
import { IMPORT_LONG_TIMEOUT_MS } from "@/modules/crm/leads/components/import-flow/constants";
import { createSlice } from "@reduxjs/toolkit";

const API_VERSION = "2.0";
const withVersion = (path) => `${path}?api-version=${API_VERSION}`;

export const importSlice = createSlice({
  name: "import",
  initialState: {
    selectedImport: {},
    mode: 1,
    /** GUID sent with CreateLeadImport — reused on upload as requestId */
    createRequestId: "",
  },

  reducers: {
    setSelectedImportToState: (state, action) => {
      state.selectedImport = action.payload;
    },
    setImportMode: (state, action) => {
      state.mode = action.payload;
    },
    setCreateRequestId: (state, action) => {
      state.createRequestId = action.payload || "";
    },
  },
});

export const {
  setSelectedImportToState,
  setImportMode,
  setCreateRequestId,
} = importSlice.actions;

export const getSelectedImport = (state) => {
  return state.import.selectedImport;
};

export const getImportMode = (state) => {
  return state.import.mode;
};

export const getCreateRequestId = (state) => {
  return state.import.createRequestId;
};

export const uploadImportedFile = (payload, params = "") => {
  let url = `${Import.upload}${params ? "?" : ""}${params}`;
  return ApiServices.postAttachements(url, payload, InstanceKeys.crmInstance);
};

/** POST /lead-imports/{id}/ai/analyze */
export const analyzeLeadImport = (recordId) =>
  ApiServices.post(
    withVersion(Import.aiAnalyze(recordId)),
    {},
    InstanceKeys.crmInstance
  );

/** POST /lead-imports/{id}/ai/save-mapping */
export const saveLeadImportMapping = (recordId, mappings) =>
  ApiServices.post(
    withVersion(Import.aiSaveMapping(recordId)),
    { mappings },
    InstanceKeys.crmInstance
  );

/** POST /lead-imports/{id}/ai/save-duplicate-settings */
export const saveLeadImportDuplicateSettings = (
  recordId,
  { fileDuplicateStrategy, crmDuplicateStrategy }
) =>
  ApiServices.post(
    withVersion(Import.aiSaveDuplicateSettings(recordId)),
    { fileDuplicateStrategy, crmDuplicateStrategy },
    InstanceKeys.crmInstance
  );

/** POST /lead-imports/{id}/ai/validate-and-prepare */
export const validateAndPrepareLeadImport = (recordId) =>
  ApiServices.post(
    withVersion(Import.aiValidateAndPrepare(recordId)),
    {},
    InstanceKeys.crmInstance,
    { timeout: IMPORT_LONG_TIMEOUT_MS }
  );

/** POST /lead-imports/{id}/ai/execute */
export const executeLeadImport = (recordId) =>
  ApiServices.post(
    withVersion(Import.aiExecute(recordId)),
    {},
    InstanceKeys.crmInstance,
    { timeout: IMPORT_LONG_TIMEOUT_MS }
  );

/** GET /lead-imports/{id}/ai/status */
export const getLeadImportAiStatus = (recordId) =>
  ApiServices.get(
    withVersion(Import.aiStatus(recordId)),
    {},
    InstanceKeys.crmInstance
  );

/** GET /lead-imports/{id}/ai/skipped-rows */
export const getLeadImportSkippedRows = (recordId) =>
  ApiServices.getFile(
    withVersion(Import.aiSkippedRows(recordId)),
    {},
    InstanceKeys.crmInstance
  );

/** DELETE /lead-imports/{id} */
export const deleteLeadImport = (recordId) => {
  ApiServices.setAuthHeader(InstanceKeys.crmInstance);
  return ApiServices.crmInstance.delete(withVersion(Import.byId(recordId)));
};

export default importSlice.reducer;
